import { API_ERROR_LABELS } from "../labels/apiErrorLabels";

function parseMaybeJson(value) {
  if (value == null) return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch {
    return null;
  }
}

/** Unwrap Lambda proxy `{ body: "<json>" }` or return the object as-is. */
export function unwrapApiErrorPayload(data) {
  if (!data) return null;
  if (typeof data === "string") {
    const parsed = parseMaybeJson(data);
    return parsed && typeof parsed === "object" ? parsed : { message: data };
  }
  if (typeof data !== "object") return null;

  if (typeof data.body === "string") {
    const parsedBody = parseMaybeJson(data.body);
    if (parsedBody && typeof parsedBody === "object") return parsedBody;
  }
  if (data.body && typeof data.body === "object") return data.body;
  return data;
}

function pickMessage(payload) {
  if (!payload || typeof payload !== "object") return null;
  const candidates = [
    payload.message,
    payload.error,
    payload.Message,
    payload.Error,
    payload.errorMessage,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    if (candidate && typeof candidate === "object" && typeof candidate.message === "string") {
      const nested = candidate.message.trim();
      if (nested) return nested;
    }
  }
  return null;
}

export function fallbackApiErrorMessage(status) {
  if (!status) return API_ERROR_LABELS.networkError;
  if (status === 400) return API_ERROR_LABELS.badRequest;
  if (status === 401) return API_ERROR_LABELS.unauthorized;
  if (status === 403) return API_ERROR_LABELS.forbidden;
  if (status === 404) return API_ERROR_LABELS.notFound;
  if (status === 409) return API_ERROR_LABELS.conflict;
  if (status === 429) return API_ERROR_LABELS.tooManyRequests;
  if (status >= 500) return API_ERROR_LABELS.serverError;
  if (status >= 400) return API_ERROR_LABELS.badRequest;
  return API_ERROR_LABELS.unknownError;
}

/** User-facing message for an axios (or similar) error. */
export function getApiErrorMessage(error) {
  const status = error?.response?.status;
  const payload = unwrapApiErrorPayload(error?.response?.data);
  const fromPayload = pickMessage(payload);
  if (fromPayload) return fromPayload;

  if (typeof error?.message === "string" && error.message && !error.response) {
    if (/network error/i.test(error.message)) return API_ERROR_LABELS.networkError;
  }

  return fallbackApiErrorMessage(status);
}

export function shouldNotifyGlobalApiError(error) {
  if (!error || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
    return false;
  }
  if (error.config?.skipGlobalErrorToast === true) {
    return false;
  }

  const status = error?.response?.status;
  if (status == null) {
    // No HTTP response — treat as network/transport failure.
    return true;
  }
  // Auth expiry is handled by session refresh / logout flows.
  if (status === 401) return false;
  return status >= 400;
}
